import { useCallback, useState } from 'react';

import ImageField from '@sayyesschool/frontend/components/image-field';
import { fieldLabels, lexemeKindOptions, lexemeTypeOptions } from '@sayyesschool/frontend/data/lexeme';
import { useMatchingLexemes } from '@sayyesschool/frontend/features/lexemes';
import { useStorageApi } from '@sayyesschool/frontend/services/storage';
import { Checkbox, Chip, ChipGroup, Flex, Form, Text } from '@sayyesschool/frontend/ui-components';

import LexemeExamples from './LexemeExamples';

import styles from './LexemeApproveForm.module.scss';

const initialRecordData = {
    translation: false,
    definition: false,
    examples: []
};

export default function LexemeApproveForm({
    lexeme,
    userId,
    onMatch,
    onSubmit,
    ...props
}) {
    const storageApi = useStorageApi();

    const [file, setFile] = useState();
    const [data, setData] = useState({
        value: lexeme.value,
        translation: lexeme.translation,
        definition: lexeme.definition ?? '',
        examples: lexeme.examples ?? []
    });
    const [recordData, setRecordData] = useState(initialRecordData);

    const matchingLexemes = useMatchingLexemes(lexeme.id, data.value);

    const handleSubmit = useCallback(async event => {
        event.preventDefault();

        const lexemeData = { ...data };

        if (file) {
            const response = await storageApi.upload(file, { path: 'lexemes' });

            lexemeData.image = {
                path: response.data.path
            };
        }

        return onSubmit({
            lexemeData,
            recordData
        });
    }, [data, file, recordData, storageApi, onSubmit]);

    const handleChange = useCallback(event => {
        const { name, value } = event.target;

        setData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleFileChange = useCallback((data, file) => {
        setFile(file);
    }, [setFile]);

    const handleFileDelete = useCallback(async image => {
        await storageApi.delete(image.path);

        setFile(undefined);

        return onSubmit({ image: undefined });
    }, [storageApi, onSubmit]);

    const handleExamplesChange = useCallback(examples => {
        setData(prev => ({ ...prev, examples }));
    }, []);

    const handleAdditionalExamplesChange = useCallback(examples => {
        setRecordData(prev => ({ ...prev, examples }));
    }, []);

    const showValueMessages = lexeme.isPending && lexeme.createdBy !== userId;

    return (
        <Form
            className={styles.root}
            onSubmit={handleSubmit}
            {...props}
        >
            <ImageField
                label="Изображение"
                image={lexeme.image || lexeme.data?.image}
                onChange={handleFileChange}
                onDelete={handleFileDelete}
            />

            <Form.Input
                label="Значение"
                name="value"
                value={data.value}
                message={matchingLexemes.length > 0 &&
                    <LexemeMatchMessage
                        matchingLexemes={matchingLexemes}
                        lexeme={lexeme}
                        onMatch={onMatch}
                    />
                }
                required
                onChange={handleChange}
            />

            {['translation', 'definition'].map(field =>
                <Form.Input
                    key={field}
                    label={fieldLabels[field]}
                    name={field}
                    value={data[field]}
                    message={showValueMessages &&
                        <LexemeValueMessage
                            currentValue={data[field]}
                            originalValue={lexeme[field]}
                            checked={recordData[field]}
                            onChecked={() =>
                                setRecordData(prev => ({
                                    ...prev,
                                    [field]: !prev[field]
                                }))
                            }
                        />
                    }
                    required
                    onChange={handleChange}
                />
            )}

            <Form.Select
                label="Тип"
                name="type"
                value={lexeme.type}
                options={lexemeTypeOptions}
                onChange={handleChange}
            />

            <Form.Select
                label="Вид"
                name="kind"
                value={lexeme.kind}
                options={lexemeKindOptions}
                onChange={handleChange}
            />

            <LexemeExamples
                examples={data.examples}
                existingExamples={lexeme.examples}
                additionalExamples={recordData.examples}
                withNotifications={showValueMessages}
                onChange={handleExamplesChange}
                onAdditionalChange={handleAdditionalExamplesChange}
            />
        </Form>
    );
}

function LexemeValueMessage({ currentValue, originalValue, committed, checked, onChecked }) {
    const showMessage =
        originalValue &&
        originalValue !== currentValue;

    return showMessage && (
        <Checkbox
            label={<>
                Добавить пользователю создавшему лексему оригинальное значение{' '}
                <i>{originalValue}</i>
            </>}
            checked={checked}
            size="sm"
            onChange={onChecked}
        />
    );
}

function LexemeMatchMessage({ matchingLexemes, lexeme, onMatch }) {
    return (
        <>
            В словаре уже есть похожие лексемы:

            <ChipGroup>
                {matchingLexemes.map(foundLexeme => (
                    <Chip
                        key={foundLexeme.id}
                        size="sm"
                        variant="soft"
                        onClick={() => onMatch(foundLexeme, lexeme)}
                    >
                        <Flex
                            gap="xxs"
                            alignItems="baseline"
                        >
                            <Text
                                content={foundLexeme.value}
                                type="body-sm"
                                inline
                            />

                            <Text
                                content={foundLexeme.translation}
                                type="body-xs"
                                inline
                            />
                        </Flex>
                    </Chip>
                ))}
            </ChipGroup>
        </>
    );
}